import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSearchParams } from 'react-router-dom';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import countryData from './CountryDDIList.json';
import confetti from 'canvas-confetti';

// Interface para país
interface Country {
  id: string;
  pais: string;
  ddi: number;
  continente: string;
  img: string;
}

// Interface para produto
interface Product {
  title: string;
  description: string;
  image: string;
  models?: {
    id: string;
    title: string;
    image: string;
  }[];
}

// Interface para campo do formulário
interface BaseField {
  id: string;
  label: string;
  placeholder?: string;
  type?: string;
  format?: (value: string) => string;
  onComplete?: (value: string) => void;
  optional?: boolean;
}

interface SingleField extends BaseField {
  isGroup: false;
}

interface GroupField extends BaseField {
  isGroup: true;
  fields: BaseField[];
}

type Question = SingleField | GroupField;

interface FormFieldProps extends BaseField {
  value?: string;
  onChange: (value: string) => void;
}

// Produtos disponíveis
const PRODUCTS: Record<string, Product> = {
  "845e91831319e89c4d656bdb80c278ac09a7230d61e5dfd2e1b1fbb436ac8917": {
    title: "Report do Grupo Waffle",
    description: "Receba um documento exclusivo contando a história da waffle",
    image: "https://app.thenewscc.com.br/report.png"
  },
  "1dcfa7e4bfff2d1a67076fde7e2e293f5de37187715082a1195469283a44c03c": {
    title: "adesivos",
    description: "você está prestes a receber um pack de adesivos para colar onde quiser! utilizaremos esses dados única e exclusivamente para envio postal nos correios.",
    image: "https://app.thenewscc.com.br/adesivos.png"
  },
  "6c024054ed780426bccb574c5a0fa28f775e550a91a88ee20158e4ddab987147": {
    title: "Porta-cartão",
    description: "Ganhe o porta-cartão do the news",
    image: "https://app.thenewscc.com.br/porta_cards.png"
  },
  "d950113d83977940d0160cdc6f59edd3da64ac03b4f7cd896a27921a45cd8fb4": {
    title: "Moleskine",
    description: "Indique amigos e ganhe o moleskine do the news",
    image: "https://app.thenewscc.com.br/moleskine.png"
  },
  "8923cac0414836b7dde6ce693aec4f8dc094093acb2c89f3937774a74830ec09": {
    title: "Caneca",
    description: "Ganhe a caneca mais cobiçada do país",
    image: "https://app.thenewscc.com.br/caneca.png"
  },
  "a4f6433a1b183ba0774b86095184ba1dda9a44e7f8d42bea2e6c967ab781c079": {
    title: "Mochila",
    description: "Indique e ganhe a mochila do dênius",
    image: "https://app.thenewscc.com.br/mochila.png"
  },
  "37c5bbe4f17965a8e4df866b6d9ae2258ee8b583d64c27b2574803985825f0e9": {
    title: "meia",
    description: "chegou a hora de reivindicar o par de meias do the news, que vai deixar seus pés ainda mais estilosos. utilizaremos esses dados única e exclusivamente para envio postal nos correios.",
    image: "https://pub-d4d36832efd647f5a00ae2d9086a65ea.r2.dev/meia.png"
  },
  "c8dacf657fe92b6064dbcde0f7888936b2c35871e6c61f7b4581accba92c278d": {
    title: "boné",
    description: "responda esse formulário para ganhar o boné do the news — o parceiro perfeito para completar seu visual. utilizaremos esses dados única e exclusivamente para envio postal nos correios.",
    image: "https://pub-d4d36832efd647f5a00ae2d9086a65ea.r2.dev/bone%CC%81.png",
    models: [
      {
        id: "model1",
        title: "06:06 club",
        image: "https://pub-d4d36832efd647f5a00ae2d9086a65ea.r2.dev/bone%CC%81.png"
      },
      {
        id: "model2",
        title: "make notícias great again",
        image: "https://assets.testeswaffle.org/f500d2dd-3ec0-4bd9-8865-8e8d32e67160.jpg"
      }
    ]
  },
  "de89f476871268f9234ac86d5973af61bc7f15acc8a686f45d4ee77992bc57fa": {
    title: "marcador de página",
    description: "seu novo marcador de páginas magnético vai chegar para acompanhar suas leituras com estilo e um toque especial do dênius! utilizaremos esses dados única e exclusivamente para envio postal nos correios.",
    image: "https://pub-d4d36832efd647f5a00ae2d9086a65ea.r2.dev/marcador%20de%20pa%CC%81gina%20(2).png"
  }
};

// Schema de validação
const formSchema = z.object({
  fullName: z.string().min(3, "O nome deve ter pelo menos 3 caracteres"),
  email: z.string().email("E-mail inválido"),
  cpf: z.string().refine((cpf) => {
    const numbers = cpf.replace(/[^\d]/g, "");
    if (numbers.length !== 11) return false;
    
    let sum = 0;
    let remainder;
    
    if (numbers === "00000000000") return false;
    
    for (let i = 1; i <= 9; i++) {
      sum = sum + parseInt(numbers.substring(i - 1, i)) * (11 - i);
    }
    
    remainder = (sum * 10) % 11;
    if (remainder === 10 || remainder === 11) remainder = 0;
    if (remainder !== parseInt(numbers.substring(9, 10))) return false;
    
    sum = 0;
    for (let i = 1; i <= 10; i++) {
      sum = sum + parseInt(numbers.substring(i - 1, i)) * (12 - i);
    }
    
    remainder = (sum * 10) % 11;
    if (remainder === 10 || remainder === 11) remainder = 0;
    if (remainder !== parseInt(numbers.substring(10, 11))) return false;
    
    return true;
  }, "CPF inválido"),
  cep: z.string().min(8, "CEP inválido"),
  street: z.string().min(3, "A rua é obrigatória"),
  number: z.string().min(1, "O número é obrigatório"),
  complement: z.string().optional(),
  neighborhood: z.string().min(3, "O bairro é obrigatório"),
  city: z.string().min(3, "A cidade é obrigatória"),
  state: z.string().length(2, "Estado inválido"),
  phone: z.string().min(10, "Telefone inválido"),
  country: z.string().min(1, "País é obrigatório")
});

type FormValues = z.infer<typeof formSchema>;

export function DeliveryForm() {
  const [searchParams] = useSearchParams();
  const [countries, setCountries] = useState<Country[]>([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(-1);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [selectedModel, setSelectedModel] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState(0);
  const [showInitialPage, setShowInitialPage] = useState(true);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');
  
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      fullName: "",
      email: "",
      cpf: "",
      cep: "",
      street: "",
      number: "",
      complement: "",
      neighborhood: "",
      city: "",
      state: "",
      phone: "",
      country: ""
    },
    mode: "onSubmit" // Só valida ao submeter
  });

  // Formata CPF
  const formatCPF = (value: string) => {
    return value
      .replace(/\D/g, '')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})/, '$1-$2')
      .replace(/(-\d{2})\d+?$/, '$1');
  };

  // Formata CEP
  const formatCEP = (value: string) => {
    return value
      .replace(/\D/g, '')
      .replace(/(\d{5})(\d)/, '$1-$2')
      .replace(/(-\d{3})\d+?$/, '$1');
  };

  // Formata telefone
  const formatPhone = (value: string, countryId: string | undefined) => {
    const country = countries.find(c => c.id === countryId);
    const phoneValue = value.replace(/\D/g, '');
    
    if (!country) {
      return phoneValue;
    }

    // Brasil
    if (country.id === 'BR') {
      if (phoneValue.length <= 10) { // Telefone fixo
        return phoneValue
          .replace(/^(\d{2})/, '($1) ')
          .replace(/(\d{4})$/, '-$1');
      } else { // Celular
        return phoneValue
          .replace(/^(\d{2})/, '($1) ')
          .replace(/(\d{5})(\d{4})$/, '$1-$2');
      }
    }
    
    // Outros países - mantém o formato original com DDI
    return phoneValue;
  };

  // Busca endereço pelo CEP
  const fetchAddress = async (cep: string) => {
    try {
      const response = await fetch(`https://viacep.com.br/ws/${cep.replace(/\D/g, '')}/json/`);
      const data = await response.json();
      
      if (!data.erro) {
        form.setValue('street', data.logradouro);
        form.setValue('neighborhood', data.bairro);
        form.setValue('city', data.localidade);
        form.setValue('state', data.uf);
      }
    } catch (error) {
      console.error('Erro ao buscar CEP:', error);
    }
  };

  // Array de questões
  const formQuestions: Question[] = [
    { id: 'fullName', label: 'Nome Completo', placeholder: 'Seu nome completo', isGroup: false },
    { id: 'email', label: 'E-mail', placeholder: 'seu@email.com', type: 'email', isGroup: false },
    { id: 'cpf', label: 'CPF', placeholder: '000.000.000-00', format: formatCPF, isGroup: false },
    { id: 'cep', label: 'CEP', placeholder: '00000-000', format: formatCEP, onComplete: fetchAddress, isGroup: false },
    { 
      id: 'address', 
      label: 'Endereço', 
      isGroup: true, 
      fields: [
        { id: 'street', label: 'Rua', placeholder: 'Nome da rua' },
        { id: 'number', label: 'Número', placeholder: '123' }
      ]
    },
    { id: 'complement', label: 'Complemento', placeholder: 'Apto 123', optional: true, isGroup: false },
    { 
      id: 'location', 
      label: 'Localização', 
      isGroup: true, 
      fields: [
        { id: 'neighborhood', label: 'Bairro', placeholder: 'Seu bairro' },
        { id: 'city', label: 'Cidade', placeholder: 'Sua cidade' }
      ]
    },
    { id: 'state', label: 'Estado', placeholder: 'UF', isGroup: false },
    { 
      id: 'contact', 
      label: 'Contato', 
      isGroup: true, 
      fields: [
        { id: 'country', label: 'País', type: 'country' },
        { id: 'phone', label: 'Telefone', placeholder: 'Digite seu telefone' }
      ]
    }
  ];

  // Função para iniciar o formulário
  const startForm = () => {
    if (selectedProduct?.models && !selectedModel) {
      return; // Não permite iniciar se houver modelos e nenhum foi selecionado
    }
    setShowInitialPage(false);
    setCurrentQuestionIndex(0);
  };

  // Busca o produto da URL
  useEffect(() => {
    const product = searchParams.get('product');
    if (product && PRODUCTS[product]) {
      setSelectedProduct(PRODUCTS[product]);
    }
    setLoading(false);
  }, [searchParams]);

  // Busca países e DDDs
  useEffect(() => {
    try {
      // Converte o objeto em array e adiciona IDs únicos
      const countriesArray = Object.entries(countryData).map(([key, country]: [string, any]) => ({
        id: key,
        pais: country.pais || '',
        ddi: Number(country.ddi) || 0,
        continente: country.continente || '',
        img: (country.img || '').replace(/\\\//g, '/')
      }));
      
      // Ordena por nome do país
      countriesArray.sort((a, b) => a.pais.localeCompare(b.pais));
      
      setCountries(countriesArray);
    } catch (error) {
      console.error('Erro ao processar dados dos países:', error);
      setCountries([]); // Garante que countries nunca será undefined
    } finally {
      setLoading(false);
    }
  }, []);

  // Calcula progresso do formulário
  useEffect(() => {
    if (currentQuestionIndex === -1) {
      setProgress(0);
    } else {
      const progress = ((currentQuestionIndex + 1) / formQuestions.length) * 100;
      setProgress(progress);
    }
  }, [currentQuestionIndex, formQuestions.length]);

  const triggerConfetti = () => {
    const duration = 3 * 1000;
    const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 0 };
    const randomInRange = (min: number, max: number) => Math.random() * (max - min) + min;

    const interval: any = setInterval(() => {
      const particleCount = 50;
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 }
      });
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 }
      });
    }, 250);

    setTimeout(() => clearInterval(interval), duration);
  };

  const onSubmit = async (data: FormValues) => {
    try {
      // Valida todos os campos obrigatórios
      const formData = form.getValues();
      const validationResult = formSchema.safeParse(formData);
      
      if (!validationResult.success) {
        const errors = validationResult.error.errors;
        errors.forEach(error => {
          form.setError(error.path[0] as any, {
            type: 'manual',
            message: error.message
          });
        });
        setError('Por favor, preencha todos os campos obrigatórios corretamente.');
        return;
      }

      const selectedModelTitle = selectedProduct?.models?.find(m => m.id === selectedModel)?.title || selectedProduct?.title;

      const payload = {
        product_id: searchParams.get('product') || '',
        product_title: selectedModelTitle || '',
        full_name: data.fullName,
        email: data.email,
        cpf: data.cpf,
        cep: data.cep,
        street: data.street,
        number: data.number,
        complement: data.complement || '',
        neighborhood: data.neighborhood,
        city: data.city,
        state: data.state,
        phone: data.phone,
        country: countries.find(c => c.id === data.country)?.pais || ''
      };

      const response = await fetch('https://backend.testeswaffle.org/webhooks/delivery-form', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.message || 'Erro ao enviar formulário');
      }

      triggerConfetti();
      setIsSuccess(true);
      setError('');
      
      setTimeout(() => {
        setIsSuccess(false);
        setShowInitialPage(true);
        setCurrentQuestionIndex(-1);
      }, 3000);

    } catch (error) {
      console.error('Erro:', error);
      setError(error instanceof Error ? error.message : 'Ocorreu um erro ao enviar o formulário. Tente novamente.');
    }
  };

  const handleNextQuestion = () => {
    const currentQuestion = formQuestions[currentQuestionIndex];
    let isValid = true;

    if (currentQuestion.isGroup) {
      currentQuestion.fields.forEach(field => {
        const value = form.getValues(field.id as any);
        if (!value && !field.optional) {
          isValid = false;
          form.setError(field.id as any, { message: 'Campo obrigatório' });
        }
      });
    } else {
      const value = form.getValues(currentQuestion.id as any);
      if (!value && !currentQuestion.optional) {
        isValid = false;
        form.setError(currentQuestion.id as any, { message: 'Campo obrigatório' });
      }
    }

    if (isValid && currentQuestionIndex < formQuestions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    }
  };

  const handlePreviousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    }
  };

  const getCurrentQuestion = () => formQuestions[currentQuestionIndex];
  
  const renderField = (field: FormFieldProps) => {
    if (field.type === 'country') {
      return (
        <FormControl>
          <Select
            value={field.value}
            onValueChange={(value) => {
              field.onChange(value);
              // Limpa o telefone quando muda o país
              form.setValue('phone', '');
            }}
          >
            <SelectTrigger className="w-full bg-gray-50 border border-gray-200">
              <SelectValue placeholder="Selecione o país">
                {field.value && (
                  <div className="flex items-center gap-2">
                    {countries.find((country) => country.id === field.value)?.img && (
                      <img
                        src={countries.find((country) => country.id === field.value)?.img}
                        alt={countries.find((country) => country.id === field.value)?.pais}
                        className="w-4 h-4 object-contain"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    )}
                    <span>
                      {countries.find((country) => country.id === field.value)?.pais}
                    </span>
                  </div>
                )}
              </SelectValue>
            </SelectTrigger>
            <SelectContent className="max-h-[300px]">
              {loading ? (
                <div className="p-4 text-sm text-center text-gray-500">
                  Carregando países...
                </div>
              ) : (
                countries.map((country) => (
                  <SelectItem
                    key={country.id}
                    value={country.id}
                    className="flex items-center gap-2 py-2"
                  >
                    <div className="flex items-center gap-2">
                      {country.img && (
                        <img
                          src={country.img}
                          alt={country.pais}
                          className="w-5 h-5 object-contain"
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = 'none';
                          }}
                        />
                      )}
                      <div className="flex flex-col">
                        <span className="font-medium">{country.pais}</span>
                        <span className="text-xs text-muted-foreground">+{country.ddi}</span>
                      </div>
                    </div>
                  </SelectItem>
                ))
              )}
            </SelectContent>
          </Select>
        </FormControl>
      );
    }
    
    if (field.id === 'phone') {
      const selectedCountry = form.getValues('country');
      const country = countries.find(c => c.id === selectedCountry);
      
      return (
        <div className="relative">
          {country && (
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
              +{country.ddi}
            </span>
          )}
          <Input
            type="tel"
            placeholder={field.placeholder}
            value={field.value}
            onChange={(e) => {
              const formattedValue = formatPhone(e.target.value, form.getValues('country'));
              field.onChange(formattedValue);
            }}
            className={`bg-gray-50 border border-gray-200 text-gray-900 placeholder-gray-500 ${
              country ? 'pl-16' : ''
            }`}
          />
        </div>
      );
    }
    
    return (
      <Input
        type={field.type || 'text'}
        placeholder={field.placeholder}
        value={field.value}
        onChange={(e) => {
          if (field.format) {
            field.onChange(field.format(e.target.value));
            if (field.onComplete && e.target.value.length === 9) {
              field.onComplete(e.target.value);
            }
          } else {
            field.onChange(e.target.value);
          }
        }}
        className="bg-gray-50 border border-gray-200 text-gray-900 placeholder-gray-500"
      />
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-[#FA2586]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white flex items-center justify-center pt-8 relative">
      <div className="w-full max-w-5xl p-6">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12 text-center"
        >
          <img 
            src="/thenews.webp" 
            alt="the news"
            className="w-48 mx-auto mb-8"
          />
          
          <AnimatePresence mode="wait">
            {showInitialPage ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.3 }}
                className="space-y-8"
              >
                {selectedProduct && (
                  <>
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.2 }}
                      className="flex flex-col md:flex-row items-center justify-center gap-8 md:gap-16"
                    >
                      {selectedProduct.models ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                          {selectedProduct.models.map((model) => (
                            <motion.div
                              key={model.id}
                              className={`cursor-pointer p-4 rounded-xl transition-all flex flex-col items-center ${
                                selectedModel === model.id
                                  ? 'ring-2 ring-yellow-400 bg-gray-50'
                                  : 'hover:bg-gray-50'
                              }`}
                              onClick={() => setSelectedModel(model.id)}
                              whileHover={{ scale: 1.02 }}
                            >
                              <img 
                                src={model.image}
                                alt={model.title}
                                className="w-32 h-32 object-contain rounded-xl shadow-sm mb-4"
                              />
                              <h3 className="text-lg font-medium text-gray-900 text-center">
                                {model.title}
                              </h3>
                            </motion.div>
                          ))}
                        </div>
                      ) : (
                        <div className="flex flex-col items-center">
                          <img 
                            src={selectedProduct.image}
                            alt={selectedProduct.title}
                            className="w-32 h-32 object-contain rounded-xl shadow-sm mb-4"
                          />
                          <h3 className="text-lg font-medium text-gray-900 text-center">
                            {selectedProduct.title}
                          </h3>
                        </div>
                      )}
                    </motion.div>
                    
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.3 }}
                      className="space-y-6"
                    >
                      <h1 className="text-4xl font-bold text-gray-900">
                        {selectedProduct.title}
                      </h1>
                      <p className="text-gray-600 text-lg max-w-xl mx-auto">
                        {selectedProduct.description}
                      </p>
                      <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-100 max-w-xl mx-auto">
                        <p className="text-yellow-800 text-sm">
                          <strong>ATENÇÃO:</strong> O prazo de entrega do prêmio é de 60 (sessenta) dias úteis a partir do preenchimento do formulário de envio.
                        </p>
                      </div>
                      <Button
                        onClick={startForm}
                        className="w-full max-w-md mx-auto bg-gray-900 hover:bg-gray-800 text-white py-6 text-lg rounded-xl"
                        disabled={selectedProduct.models && !selectedModel}
                      >
                        {selectedProduct.models && !selectedModel
                          ? "Selecione um modelo para continuar"
                          : "Começar Formulário →"}
                      </Button>
                    </motion.div>
                  </>
                )}
              </motion.div>
            ) : isSuccess ? (
              <motion.div 
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                className="bg-white rounded-3xl p-12 shadow-sm border border-gray-100 max-w-3xl mx-auto text-center"
              >
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", duration: 0.5 }}
                  className="flex items-center justify-center w-20 h-20 rounded-full bg-gray-900 text-white mx-auto mb-6"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </motion.div>
                <h2 className="text-3xl font-bold text-gray-900 mb-4">Resposta enviada com sucesso! 🎉</h2>
                <p className="text-xl text-gray-700 font-['Inter']">Seu prêmio será enviado em breve!</p>
              </motion.div>
            ) : (
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                  <motion.div
                    key={currentQuestionIndex}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.5 }}
                    className="bg-white rounded-3xl p-8 md:p-12 shadow-sm border border-gray-100 max-w-3xl mx-auto"
                  >
                    {getCurrentQuestion().isGroup ? (
                      <div className="space-y-6">
                        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">
                          {getCurrentQuestion().label}
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          {(getCurrentQuestion() as GroupField).fields.map((field) => (
                            <FormField
                              key={field.id}
                              control={form.control}
                              name={field.id as any}
                              render={({ field: fieldProps }) => (
                                <FormItem>
                                  <FormLabel className="text-gray-700 text-lg">{field.label}</FormLabel>
                                  <FormControl>
                                    {renderField({ ...field, ...fieldProps })}
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-6">
                        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">
                          {getCurrentQuestion().label}
                        </h2>
                        <FormField
                          control={form.control}
                          name={getCurrentQuestion().id as any}
                          render={({ field }) => (
                            <FormItem>
                              <FormControl>
                                {renderField({ ...getCurrentQuestion() as SingleField, ...field })}
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    )}

                    <div className="mt-8 flex justify-between items-center">
                      <button
                        type="button"
                        onClick={handlePreviousQuestion}
                        disabled={currentQuestionIndex === 0}
                        className="text-base md:text-lg text-gray-500 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        ← Anterior
                      </button>
                      {currentQuestionIndex === formQuestions.length - 1 ? (
                        <Button
                          type="submit"
                          className="bg-gray-900 hover:bg-gray-800 text-white px-8 py-6 text-lg rounded-xl"
                          disabled={form.formState.isSubmitting}
                        >
                          {form.formState.isSubmitting ? (
                            <div className="w-6 h-6 border-4 border-white border-t-transparent rounded-full animate-spin" />
                          ) : (
                            'Enviar'
                          )}
                        </Button>
                      ) : (
                        <Button
                          type="button"
                          onClick={handleNextQuestion}
                          className="bg-gray-900 hover:bg-gray-800 text-white px-8 py-6 text-lg rounded-xl"
                        >
                          Próximo →
                        </Button>
                      )}
                    </div>
                  </motion.div>

                  <div className="mt-4 flex justify-center items-center space-x-4">
                    <div className="h-2 w-48 bg-gray-200 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gray-900 transition-all duration-300"
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                    <span className="text-base md:text-lg text-gray-500">
                      {currentQuestionIndex + 1} de {formQuestions.length}
                    </span>
                  </div>

                  {error && (
                    <motion.p
                      initial={{ opacity: 0, y: 5 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="text-red-500 text-center mt-2"
                    >
                      {error}
                    </motion.p>
                  )}
                </form>
              </Form>
            )}
          </AnimatePresence>
        </motion.div>

        {/* Barra de Progresso Fixa */}
        <div className="fixed bottom-0 left-0 w-full h-2 bg-gray-200">
          <motion.div 
            className="h-full bg-[#ffd000]"
            initial={{ width: "0%" }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.3 }}
          />
        </div>
      </div>
    </div>
  );
} 